import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  History,
  Settings2,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { USER_ROLES } from '@/lib/constants/roles';
import { usePermission } from '@/lib/permissions/use-permission';
import { formatDateTime } from '@/lib/utils/date';
import { useDeleteIvr, useIvrDetail, useIvrList } from '../api';
import type { IvrListRow } from '../types';

export function IvrListPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  // Only end-users can author IVR flows. Reseller/SADMIN/SUB_SADMIN can
  // browse the listing but not create.
  const canCreate = role === USER_ROLES.USER;
  const canDelete = usePermission('Manage Campaign.Custom IVR List', BASE_PERMISSION.DELETE);
  const query = useIvrList();
  const del = useDeleteIvr();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<IvrListRow | null>(null);
  const [logsId, setLogsId] = useState<number | null>(null);
  const logs = useIvrDetail(logsId);

  const columns = useMemo<ColumnDef<IvrListRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'flowName',
        header: 'IVR name',
        cell: ({ row }) => (
          <Link
            to={canCreate ? `/ivr/${row.original.id}` : `/ivr/${row.original.id}?view=1`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.flowName}
          </Link>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        size: 200,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        size: 200,
        cell: ({ row }) => formatDateTime(row.original.updatedAt),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: ({ row }) => {
          const completed = String(row.original.status) === '1';
          return (
            <Badge
              variant="outline"
              className={
                completed
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                  : 'border-amber-300 bg-amber-50 text-amber-900'
              }
            >
              {completed ? 'Completed' : 'Pending'}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{row.original.flowName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canCreate && (
                <DropdownMenuItem
                  onClick={() => navigate(`/ivr/${row.original.id}`)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setLogsId(row.original.id)}>
                <History className="h-4 w-4" />
                View logs
              </DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => setPendingDelete(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate, canCreate, canDelete],
  );

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted "${pendingDelete.flowName}"`);
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the IVR.'),
    });
  };

  const canView = usePermission('Manage Campaign.Custom IVR List', BASE_PERMISSION.VIEW);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="IVR Designer"
        description="Visual call flows your inbound + outbound campaigns can drive."
        actions={
          canCreate ? (
            <Button asChild>
              <Link to="/add-ivr">
                <Plus className="h-4 w-4" />
                New IVR
              </Link>
            </Button>
          ) : null
        }
      />

      <DataTable<IvrListRow, unknown>
        tableId="ivr:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search IVRs…"
        emptyTitle="No IVR flows yet"
        emptyDescription="Create your first flow to start routing inbound calls."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="custom-ivr.csv"
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            IVR{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.flowName}
            </span>{' '}
            will be permanently deleted. Campaigns using it will fail until they
            are reassigned.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />

      <Dialog
        open={logsId !== null}
        onOpenChange={(o) => !o && setLogsId(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update logs</DialogTitle>
            <DialogDescription>
              When this IVR has been edited.
            </DialogDescription>
          </DialogHeader>
          {logs.isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : !logs.data?.updationLogs?.length ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No update logs available.
            </div>
          ) : (
            <ol className="max-h-[40vh] space-y-1 overflow-y-auto">
              {logs.data.updationLogs.map((log, i) => (
                <li
                  key={i}
                  className="rounded border border-border bg-muted/30 px-3 py-2 text-xs"
                >
                  {log.updationAt ?? '—'}
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </FillPage>
  );
}